import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useApi } from "providers/Api";
import { useBoard } from "providers/Board";
import { useToasts } from "providers/Toasts";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo } from "react";

import { BoardRunsContext } from "./BoardRunsContext";

export const BoardRunsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { boardApi } = useApi();
  const { board } = useBoard();
  const i18n = useTranslations();
  const toasts = useToasts();

  const boardId = board?.id;

  const { isLoading, refetch, data, error } = useQuery({
    queryKey: ["boardruns", boardId],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return boardApi.getBoardRuns(boardId, signal);
    },
    enabled: !!boardId,
  });

  const reloadBoardRuns = useCallback(async () => {
    const result = await refetch();
    if (result.data) {
      toasts.refreshSuccess(i18n.Runs, undefined);
    }
  }, [refetch, i18n, toasts]);

  const contextValue = useMemo(
    () => ({
      boardId,
      runs: isLoading ? [] : (data?.runs ?? []),
      canAddRun: data?.canAddRun ?? false,
      error: error as AxiosError | undefined,
      isLoading,
      reloadBoardRuns,
    }),
    [boardId, data, isLoading, error, reloadBoardRuns]
  );

  return <BoardRunsContext.Provider value={contextValue}>{children}</BoardRunsContext.Provider>;
};
